import { useState } from "react";
import { useSettingsStore, type Quality } from "../../app/settingsStore";
import { playCue, unlockAudio } from "../../audio/AudioEngine";
import { TUTORIAL_STAGE } from "@reagent/content";
import { useGameStore } from "../../app/gameStore";
import "./settings.css";

/**
 * Player settings, as opposed to the tuning console next door — that one is
 * for balancing the game, this one is for playing it. Sound, motion and how
 * hard to push the GPU are decisions a player makes about their own machine
 * and their own comfort, and they should not have to open a developer panel
 * to find them.
 *
 * Collapsed to a single button by default, because a settings drawer sitting
 * open over the board is worse than no settings drawer.
 */
const QUALITIES: ReadonlyArray<{ value: Quality; label: string; hint: string }> = [
  { value: "high", label: "high", hint: "everything: bloom, grain, full resolution" },
  { value: "medium", label: "medium", hint: "keeps the bloom, drops the grain and the pixels" },
  { value: "low", label: "low", hint: "no post-processing — for integrated graphics" },
];

export function SettingsPanel() {
  const [open, setOpen] = useState(false);
  const { quality, muted, volume, reduceMotion, showPerf, set } = useSettingsStore();
  const selectStage = useGameStore((s) => s.selectStage);

  return (
    <div className={`settings${open ? " is-open" : ""}`}>
      <button
        className="settings__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title="settings"
      >
        {muted ? "🔇" : "🔊"} settings
      </button>

      {open && (
        <div className="settings__panel">
          <fieldset className="settings__group">
            <legend>sound</legend>
            <label className="settings__row settings__row--check">
              <input
                type="checkbox"
                checked={!muted}
                onChange={(e) => {
                  // Un-muting is itself a gesture, so it is a fine moment to
                  // start the audio context — otherwise the first thing a
                  // player does after turning sound on is hear nothing.
                  unlockAudio();
                  set({ muted: !e.target.checked });
                  if (e.target.checked) playCue("uiClick");
                }}
              />
              sound on
            </label>
            <label className="settings__row">
              volume
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round(volume * 100)}
                disabled={muted}
                onChange={(e) => set({ volume: Number(e.target.value) / 100 })}
                onPointerUp={() => playCue("uiClick")}
              />
            </label>
          </fieldset>

          <fieldset className="settings__group">
            <legend>graphics</legend>
            {QUALITIES.map((q) => (
              <label key={q.value} className="settings__row settings__row--check" title={q.hint}>
                <input
                  type="radio"
                  name="quality"
                  checked={quality === q.value}
                  onChange={() => set({ quality: q.value })}
                />
                {q.label}
              </label>
            ))}
            <label className="settings__row settings__row--check">
              <input
                type="checkbox"
                checked={showPerf}
                onChange={(e) => set({ showPerf: e.target.checked })}
              />
              show frame time
            </label>
          </fieldset>

          <fieldset className="settings__group">
            <legend>accessibility</legend>
            <label
              className="settings__row settings__row--check"
              title="damps the camera drift, the dust and the effect travel"
            >
              <input
                type="checkbox"
                checked={reduceMotion}
                onChange={(e) => set({ reduceMotion: e.target.checked })}
              />
              reduce motion
            </label>
            <p className="settings__note">
              Reagents are drawn as shapes as well as colours, always — the board never asks you to
              tell two streams apart by hue alone.
            </p>
          </fieldset>

          <button className="settings__replay" onClick={() => selectStage(TUTORIAL_STAGE.id)}>
            replay the induction
          </button>
        </div>
      )}
    </div>
  );
}
